<script setup>
import { ref, computed, toRef, watch, onMounted } from 'vue';
import { useCustomerStore } from '@client/js/store/customer';
import useCartStore from '@client/js/store/cart';
import ErrorNote from '@client/js/components/ErrorNote.vue';
import AddressAutocomplete from '@client/js/components/AddressAutocomplete.vue';
import { useAddressAutocomplete } from '@client/js/composables/useAddressAutocomplete';
import { useDeliveryZone } from '@client/js/composables/useDeliveryZone';
import { useUberQuote } from '@client/js/composables/useUberQuote';
import { fromGooglePayload, makeAddress } from '@client/js/shared/address';

const customerStore = useCustomerStore();
const cartStore = useCartStore();

const delivery = window?._EZCL?.data?.delivery || {};
const apiKey = window?._EZCL?.data?.googleMapsKey || '';

const { state: acState, load, degrade, allowSession } = useAddressAutocomplete();

// Autocomplete mode only when the server says it's available AND it hasn't
// degraded at runtime. Otherwise the classic separate fields are shown.
const autocompleteMode = computed(
  () => delivery?.autocomplete?.available && acState.value !== 'degraded'
);

// In autocomplete mode the classic fields stay hidden until the user clicks
// Edit (or no selection has been made yet).
const showManualFields = ref(false);
const hasSelection = computed(() => !!customerStore.formattedAddress);

onMounted(() => {
  if (delivery?.autocomplete?.available) load(apiKey);
});

const handleSelect = (payload) => {
  customerStore.applyAddress(fromGooglePayload(payload));
  showManualFields.value = false;
};
const handleError = (err) => degrade(err);

const editManually = () => { showManualFields.value = true; };

// Any manual edit invalidates Google-derived coords so the zone gate fails
// open rather than trusting a stale location.
const onManualInput = () => customerStore.clearAddressCoords();

// Drive the store's zoneStatus from the gate so both checkout flows can block
// on 'outside' via customerStore.deliveryBlocked.
const address = computed(() =>
  makeAddress({ lat: customerStore.lat, lng: customerStore.lng })
);
const { zoneStatus } = useDeliveryZone(toRef(customerStore, 'orderType'), address);
watch(zoneStatus, (s) => { customerStore.zoneStatus = s; }, { immediate: true });

// Live Uber Direct quote (only active when the site offers it; otherwise idle).
const uberAddress = computed(() => ({
  street: customerStore.street,
  city: customerStore.city,
  state: customerStore.state,
  zip: customerStore.zip,
  country: 'US',
}));
const { status: uberStatus, feeCents: uberFeeCents, etaMinutes: uberEta, quoteId: uberQuoteId, refetch: refetchUberQuote } =
  useUberQuote(toRef(customerStore, 'orderType'), uberAddress);

// Persist the quote into the store so checkout sends quoteId and the totals
// show the live fee. Store stays the single source of truth for both flows.
watch([uberStatus, uberFeeCents, uberEta, uberQuoteId], () => {
  customerStore.uberQuote = {
    status: uberStatus.value,
    feeCents: uberFeeCents.value,
    etaMinutes: uberEta.value,
    quoteId: uberQuoteId.value,
  };
}, { immediate: true });

const uberFeeLabel = computed(() => {
  if (uberStatus.value !== 'ok' || uberFeeCents.value == null) return '';
  const dollars = (uberFeeCents.value / 100).toFixed(2);
  return uberEta.value ? `$${dollars} · ~${uberEta.value} min` : `$${dollars}`;
});

// The status copy comes from the cart store's deliveryDisplay descriptor — the
// single source of truth — so this field and the totals row stay in lock-step.
const deliveryDisplay = computed(() => cartStore.deliveryDisplay);
</script>

<template>
  <div>
    <template v-if="autocompleteMode">
      <div class="text-field">
        <AddressAutocomplete
          placeholder="Start typing your address"
          :countries="['us']"
          :allow-session="allowSession"
          @select="handleSelect"
          @error="handleError"
        />
      </div>

      <p v-if="hasSelection" class="selected-address">
        {{ customerStore.formattedAddress }}
        <button type="button" class="edit-address" @click="editManually">Edit</button>
      </p>
    </template>

    <div v-show="!autocompleteMode || showManualFields">
      <div class="text-field">
        <input name="street" type="text" v-model="customerStore.street"
          v-bind="customerStore.streetProps" @input="onManualInput" />
        <label class="fieldLabel" for="address">Address</label>
        <ErrorNote :message="customerStore.errors.street" />
      </div>
      <div class="text-field">
        <input name="city" type="text" v-model="customerStore.city"
          v-bind="customerStore.cityProps" @input="onManualInput" />
        <label class="fieldLabel" for="address">City</label>
        <ErrorNote :message="customerStore.errors.city" />
      </div>
      <div class="text-field">
        <input name="state" type="text" placeholder="State / Province" v-model="customerStore.state"
          v-bind="customerStore.stateProps" @input="onManualInput" />
        <label class="fieldLabel" for="address">State / Province</label>
        <ErrorNote :message="customerStore.errors.state" />
      </div>
      <div class="text-field">
        <input name="zip" type="text" placeholder="Postal code" v-model="customerStore.zip" maxlength="10"
          v-bind="customerStore.zipProps" @input="onManualInput" />
        <label class="fieldLabel" for="address">Zip</label>
        <ErrorNote :message="customerStore.errors.zip" />
      </div>
    </div>

    <p v-if="customerStore.deliveryBlocked" class="zone-unavailable">
      Delivery is not available to the selected address.
    </p>

    <!-- The live Uber quote (fee/ETA/coverage) is suppressed once the radius
         gate blocks the address, so the zone message above is never contradicted
         by a "$7.99 · ~49 min" line for an address we can't actually deliver to. -->
    <template v-else>
      <div v-if="uberStatus === 'loading'" class="uber-quote uber-quote--muted" role="status" aria-live="polite">
        <span class="uber-spinner" aria-hidden="true"></span>
        {{ deliveryDisplay.note }}
      </div>
      <p v-else-if="uberStatus === 'ok'" class="uber-quote uber-quote--ok" aria-live="polite">
        <span class="uber-dot" aria-hidden="true"></span>
        Delivery: <strong>{{ uberFeeLabel }}</strong>
      </p>
      <p v-else-if="uberStatus === 'no_coverage'" class="uber-quote uber-quote--warn" aria-live="polite">
        <span class="uber-dot" aria-hidden="true"></span>
        {{ deliveryDisplay.note }}
      </p>
      <p v-else-if="uberStatus === 'unavailable'" class="uber-quote uber-quote--warn" aria-live="polite">
        <span class="uber-dot" aria-hidden="true"></span>
        {{ deliveryDisplay.note }}
        <button v-if="deliveryDisplay.retryable" type="button" class="uber-retry" @click="refetchUberQuote">Try again</button>
      </p>
    </template>
  </div>
</template>

<style scoped>
.selected-address {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin: -1rem 0 1.5rem;
  font-size: 0.9375rem;
  color: var(--text-secondary);
}
.edit-address {
  background: none;
  border: none;
  color: var(--c-primary);
  cursor: pointer;
  font-size: 0.8125rem;
  text-decoration: underline;
}
.zone-unavailable {
  color: #b00020;
  font-size: 0.8125rem;
  margin-top: -0.5rem;
}
.uber-quote {
  font-size: 0.875rem;
  margin-top: -0.5rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.uber-quote--warn { color: #b00020; }
.uber-quote--muted { color: var(--text-secondary); }
.uber-quote--ok { color: #1a7f37; }

.uber-dot {
  flex: none;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: currentColor;
}

.uber-spinner {
  flex: none;
  width: 0.85rem;
  height: 0.85rem;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  opacity: 0.6;
  animation: uber-spin 0.7s linear infinite;
}
@keyframes uber-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) {
  .uber-spinner { animation: none; }
}

.uber-retry {
  background: none;
  border: none;
  padding: 0;
  margin-left: 0.25rem;
  color: var(--c-primary);
  cursor: pointer;
  font-size: inherit;
  text-decoration: underline;
}
</style>
